import { useMemo, useEffect } from 'react'
import { getFromLocalStorage, useDigitalSign, useToggle } from '@/shared'
import { Button } from '@/ui'
import { Book } from '@app/images'
import { useTranslation } from 'react-i18next'
import { Form } from './form'
import { initialColContract } from '@/entity/collectiveContracts/model/reducer'
import { useSelector, useDispatch } from 'react-redux'
import { handbookSelectors } from '@/entity/handbook'
import { colContractsActions, colContractsSelectors, colContractsThunks } from '@/entity'

export const RegistrationCollectiveContract = ({ fetchData, isEdit, openEdit }) => {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const [isCreate, toggleCreate] = useToggle(false)
	const colContractXML = useSelector(colContractsSelectors.colXml)
	const isSuccess = useSelector(colContractsSelectors.isSuccess)
	const contract = useSelector(colContractsSelectors.colContract)
	const activeCompany = getFromLocalStorage('activeCompany')

	const { successPayload, onSign, failedPayload, onClearPayload } = useDigitalSign()

	const contractTerms = useSelector(handbookSelectors.contractTerms)
	const extraGuaranteesCollectiveContract = useSelector(handbookSelectors.extraGuaranteesCollectiveContract)
	const unionTypeCollectiveContract = useSelector(handbookSelectors.unionTypeCollectiveContract)
	const positionsByKnz = useSelector(handbookSelectors.positionsByKnz)
	const workCondition = useSelector(handbookSelectors.workCondition)
	const ccEnterpriseType = useSelector(handbookSelectors.ccEnterpriseType)
	const ccOwnershipType = useSelector(handbookSelectors.ccOwnershipType)
	const relaxTimeCollectiveContract = useSelector(handbookSelectors.relaxTimeCollectiveContract)
	const workTimeCollectiveContract = useSelector(handbookSelectors.workTimeCollectiveContract)

	const options = useMemo(() => ({
		contractTerms,
		extraGuaranteesCollectiveContract,
		unionTypeCollectiveContract,
		positionsByKnz,
		workCondition,
		ccEnterpriseType,
		ccOwnershipType,
		relaxTimeCollectiveContract,
		workTimeCollectiveContract,
	}), [
		contractTerms,
		extraGuaranteesCollectiveContract,
		unionTypeCollectiveContract,
		positionsByKnz,
		workCondition,
		ccEnterpriseType,
		ccOwnershipType,
		relaxTimeCollectiveContract,
		workTimeCollectiveContract,
	])
	const onClear = () => {
		onClearPayload()
		dispatch(colContractsActions.setColContractSuccess(false))
		dispatch(colContractsActions.resetColXml())
	}
	useEffect(() => {
		if (successPayload) {
			dispatch(colContractsThunks.sendSignedColContract(successPayload))
			onClearPayload()
		}
	}, [successPayload])

	useEffect(() => {
		failedPayload && onClear()
	}, [failedPayload])

	useEffect(() => colContractXML && onSign(colContractXML), [colContractXML])

	const Close = () => {
		toggleCreate(false)
		openEdit(false)
		dispatch(colContractsActions.setColContract(initialColContract))
	}

	useEffect(() => {
		if (isSuccess) {
			onClear()
			fetchData()
			Close()
		}
		return onClear()
	}, [isSuccess])

	let colContract =
		isEdit && contract.externalId
			? {
					...initialColContract,
					...contract,
					...contract.collectiveContractDetails,
					...contract.collectiveContractDetails.rateInfo,
					externalId: contract.externalId,
					enterpriseName: contract.companyName,
					enterpriseNameKaz: contract.companyNameKaz,
					overtimePayIs: !!contract.collectiveContractDetails.overtimePay,
					holidayPayIs: !!contract.collectiveContractDetails.holidayPay,
					nightTimePayIs: !!contract.collectiveContractDetails.nightTimePay,
					combinationPositionPayIs: !!contract.collectiveContractDetails.combinationPositionPay,
					termCompensationPayIs: !!contract.collectiveContractDetails.termCompensationPay,
					wayCompensationPayIs: !!contract.collectiveContractDetails.wayCompensationPay,
					wageIndexProcedureIs: !!contract.collectiveContractDetails.wageIndexProcedure,
					industryCodes: contract.industryCodes || [],
					katoAB: activeCompany?.ddistrictCode?.substring(0, 2) || '',
			}
			: {
				...initialColContract,
				externalId: null,
				...(!activeCompany?.collectiveContract ? {
					bin: activeCompany?.bin || '',
					enterpriseName: activeCompany?.companyName || '',
					enterpriseNameKaz: activeCompany?.companyNameKaz || '',
				} : {}),
				katoAB: activeCompany?.ddistrictCode?.substring(0, 2) || '',
			}

	const onSubmit = (data) => {
		let transformedColContract = {
			bin: data.bin,
			collectiveContractDetails: {
				dcolContractEnterpriseTypeCode: data.dcolContractEnterpriseTypeCode,
				dcolContractOwnershipTypeCode: data.dcolContractOwnershipTypeCode,
				fundingAmount: data.fundingAmount,
				relaxTime: data.relaxTime,
				securityFundingVolume: data.securityFundingVolume,
				workTime: data.workTime,
			},
			collectiveContractNumber: data.collectiveContractNumber,
			companyName: data.enterpriseName,
			companyNameKaz: data.enterpriseNameKaz,
			dateFrom: data.dateFrom,
			dcontractDateCode: data.dcontractDateCode,
			dateTo: data.dateTo,
			externalId: data.externalId,
			files: data.files,
			fileProtocolDisagreement: data.fileProtocolDisagreement?.[0],
			unions: data.unions,
			extraGuarantee: data.extraGuarantee,
			positions: data.positions,
			industryCodes: data.industryCodes || [],
			katoAB: data.katoAB,
		}
		// Добавляем overtimePay только если overtimePayIs равно true
		if (data.overtimePayIs) {
			transformedColContract.collectiveContractDetails.overtimePay = data.overtimePay
		}
		if (data.holidayPayIs) {
			transformedColContract.collectiveContractDetails.holidayPay = data.holidayPay
		}
		if (data.nightTimePayIs) {
			transformedColContract.collectiveContractDetails.nightTimePay = data.nightTimePay
		}
		if (data.combinationPositionPayIs) {
			transformedColContract.collectiveContractDetails.combinationPositionPay = data.combinationPositionPay
		}
		if (data.termCompensationPayIs) {
			transformedColContract.collectiveContractDetails.termCompensationPay = data.termCompensationPay
		}
		if (data.wayCompensationPayIs) {
			transformedColContract.collectiveContractDetails.wayCompensationPay = data.wayCompensationPay
		}
		if (data.wageIndexProcedureIs) {
			transformedColContract.collectiveContractDetails.wageIndexProcedure = data.wageIndexProcedure
		}
		dispatch(colContractsThunks.generateXML(transformedColContract))
	}

	return (
		<div>
			<Button icon={Book} iconSize={'big'} onClick={toggleCreate}>
				{t('contracts_tab.registration_collective_contract')}
			</Button>
			{isCreate || (isEdit && colContract.externalId) ? (
				<Form initialValues={colContract} onCancel={Close} options={options} onSubmit={onSubmit} isEdit={isEdit}></Form>
			) : null}
		</div>
	)
}
